import React, { useState, useRef, useEffect } from 'react';
import { Mail, Linkedin, Send, User, MessageSquare, Zap, Github, BookOpen } from 'lucide-react';
import { contactInfo } from '../data/mockData';
import { useTheme } from '../contexts/ThemeContext';
import { trackButtonClick } from '../utils/trackButtonClick';

// Turns a Retry-After value (seconds) into a phrase for the rate-limit error
// message, so "please try again" reflects the caller's actual remaining wait
// (up to 24h, per contactDailyLimiter/contactEmailLimiter) instead of a fixed
// generic phrase regardless of whether it's 2 minutes or 23 hours away.
function formatRetryAfter(seconds) {
  const minutes = Math.round(seconds / 60);
  if (minutes <= 1) return 'in about a minute';
  if (minutes < 60) return `in about ${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in about ${hours} hour${hours === 1 ? '' : 's'}`;
  return 'tomorrow';
}

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const { isDark, background, text, accents } = useTheme();

  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  // Explicit render (rather than the auto-render `cf-turnstile` class) so we
  // can read the token via window.turnstile.getResponse() at submit time and
  // reset the widget after every attempt — the token is single-use and
  // expires after a few minutes, so it can't just be read once on mount.
  useEffect(() => {
    if (!turnstileSiteKey) return;

    let cancelled = false;
    const renderWidget = () => {
      if (cancelled || !window.turnstile || !turnstileRef.current || turnstileWidgetId.current) return;
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        theme: isDark ? 'dark' : 'light',
      });
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval);
        renderWidget();
      }
    }, 100);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [turnstileSiteKey]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 20;
      
      if (formRef.current) {
        formRef.current.style.transform = `perspective(1000px) rotateX(${-y * 0.5}deg) rotateY(${x * 0.5}deg) translateZ(20px)`;
      }
    };

    const section = sectionRef.current;
    if (section) {
      section.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (section) {
        section.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const turnstileToken = turnstileWidgetId.current !== null
      ? window.turnstile?.getResponse(turnstileWidgetId.current)
      : null;

    if (turnstileSiteKey && !turnstileToken) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the verification challenge before sending.'
      });
      setTimeout(() => setSubmitStatus(null), 8000);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Get backend URL from environment
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          turnstileToken
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 429) {
          const retryAfterSeconds = parseInt(response.headers.get('Retry-After'), 10);
          const wait = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
            ? formatRetryAfter(retryAfterSeconds)
            : 'shortly';
          throw new Error(`You've reached the message limit. Please try again ${wait}.`);
        }

        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      setSubmitStatus({
        type: 'success',
        message: result.message || 'Message sent successfully!'
      });

      trackButtonClick({ section: 'contact', label: 'Send Message', type: 'form_submit', detail: 'success' });

      // Clear form
      setFormData({ name: '', email: '', subject: '', message: '' });

    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to send message. Please try again or contact me directly via email or LinkedIn.'
      });

      trackButtonClick({ section: 'contact', label: 'Send Message', type: 'form_submit', detail: 'error' });
    } finally {
      if (turnstileWidgetId.current !== null) {
        window.turnstile?.reset(turnstileWidgetId.current);
      }
      setIsSubmitting(false);

      // Clear status after 8 seconds
      setTimeout(() => setSubmitStatus(null), 8000);
    }
  };

  const contactMethods = [
    {
      icon: Mail,
      label: 'Email',
      value: contactInfo.email,
      href: `mailto:${contactInfo.email}`,
      color: "from-blue-400 to-blue-600",
      glow: "shadow-blue-500/30"
    },
    {
      icon: Linkedin,
      label: 'LinkedIn',
      value: 'Connect with me',
      href: contactInfo.linkedin,
      color: "from-blue-500 to-blue-700",
      glow: "shadow-blue-600/30"
    }
  ];

  return (
    <section id="contact" ref={sectionRef} className={`min-h-screen py-20 ${background.section} relative overflow-hidden transition-all duration-500`}>
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Floating particles */}
        {Array.from({ length: 50 }).map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 ${isDark ? 'bg-white' : 'bg-gray-400'} rounded-full ${isDark ? 'opacity-20' : 'opacity-30'} animate-pulse`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          ></div>
        ))}
        
        {/* Large glowing orbs */}
        <div className={`absolute top-1/4 left-1/4 w-64 h-64 ${isDark ? 'bg-blue-500/10' : 'bg-blue-200/30'} rounded-full blur-3xl animate-pulse`}></div>
        <div className={`absolute bottom-1/4 right-1/4 w-96 h-96 ${isDark ? 'bg-purple-500/10' : 'bg-purple-200/30'} rounded-full blur-3xl animate-pulse delay-1000`}></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12 sm:mb-16">
            <div className={`inline-block px-4 py-2 ${background.card} rounded-full ${accents.contact} font-mono text-sm mb-6`}>
              &gt; contact --init
            </div>
            <h2 className={`text-4xl sm:text-5xl lg:text-7xl font-bold ${text.gradient} mb-6 leading-relaxed`}>
              Let's Connect
            </h2>
            <p className={`text-lg sm:text-xl ${text.secondary} max-w-3xl mx-auto px-4`}>
              Excited to craft the next groundbreaking iOS, iPadOS, or macOS app?<br />
              Let's explore how we can turn your vision into reality.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            {/* Left Side - Contact Info & Social */}
            <div className="space-y-6 sm:space-y-8 order-2 lg:order-1">
              {/* Contact Methods */}
              <div className="space-y-4 sm:space-y-6">
                {contactMethods.map((method, index) => {
                  const Icon = method.icon;
                  return (
                    <a
                      key={index}
                      href={method.href}
                      target={method.label === 'LinkedIn' ? '_blank' : undefined}
                      rel={method.label === 'LinkedIn' ? 'noopener noreferrer' : undefined}
                      onClick={() => trackButtonClick({ section: 'contact', label: method.label, type: 'contact_method' })}
                      className="group block"
                    >
                      <div className={`flex items-center space-x-4 sm:space-x-6 p-4 sm:p-6 ${background.card} rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 overflow-hidden`}>
                        {/* Background glow effect */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${method.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                        
                        <div className={`relative z-10 p-3 sm:p-4 bg-gradient-to-br ${method.color} rounded-xl ${method.glow} group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        
                        <div className="relative z-10 flex-1 min-w-0">
                          <h3 className={`text-base sm:text-lg font-semibold ${text.primary} mb-1 group-hover:${accents.contact} transition-colors truncate`}>
                            {method.label}
                          </h3>
                          <p className={`text-sm sm:text-base ${text.secondary} group-hover:${text.primary} transition-colors truncate`}>
                            {method.value}
                          </p>
                        </div>

                        <div className="relative z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <Zap className="w-5 h-5 text-green-400" />
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>

              {/* Status Badge */}
              <div className="relative">
                <div className={`bg-gradient-to-br ${isDark ? 'from-green-500/20 to-green-600/20 border-green-500/30' : 'from-green-100 to-green-200 border-green-300'} border rounded-2xl p-4 sm:p-6 shadow-lg ${isDark ? 'shadow-green-500/20' : 'shadow-green-500/10'}`}>
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="relative flex-shrink-0">
                      <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-400 rounded-full animate-ping"></div>
                      <div className="absolute top-0 left-0 w-3 h-3 sm:w-4 sm:h-4 bg-green-400 rounded-full"></div>
                    </div>
                    <div className="min-w-0">
                      <h3 className={`text-base sm:text-lg font-semibold mb-1 ${isDark ? 'text-green-300' : 'text-green-800'}`}>Focused & Happily Contributing</h3>
                      <p className={`text-sm truncate ${isDark ? 'text-green-200' : 'text-green-700'}`}>Thriving in my current opportunities</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex space-x-3 sm:space-x-4">
                <a
                  href={contactInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick({ section: 'contact', label: 'LinkedIn', type: 'social_link' })}
                  className={`group p-3 sm:p-4 ${background.card} rounded-xl hover:scale-110 transition-all duration-300`}
                >
                  <Linkedin className={`w-5 h-5 sm:w-6 sm:h-6 ${text.muted} group-hover:text-blue-400 transition-colors`} />
                </a>
                <a
                  href={`mailto:${contactInfo.email}`}
                  onClick={() => trackButtonClick({ section: 'contact', label: 'Email', type: 'social_link' })}
                  className={`group p-3 sm:p-4 ${background.card} rounded-xl hover:scale-110 transition-all duration-300`}
                >
                  <Mail className={`w-5 h-5 sm:w-6 sm:h-6 ${text.muted} group-hover:text-green-400 transition-colors`} />
                </a>
                <a
                  href="https://medium.com/@rahulsingh05"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick({ section: 'contact', label: 'Medium', type: 'social_link' })}
                  className={`group p-3 sm:p-4 ${background.card} rounded-xl hover:scale-110 transition-all duration-300`}
                >
                  <BookOpen className={`w-5 h-5 sm:w-6 sm:h-6 ${text.muted} group-hover:text-orange-400 transition-colors`} />
                </a>
                <a
                  href="https://github.com/rahulsingh2k10"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick({ section: 'contact', label: 'GitHub', type: 'social_link' })}
                  className={`group p-3 sm:p-4 ${background.card} rounded-xl hover:scale-110 transition-all duration-300`}
                >
                  <Github className={`w-5 h-5 sm:w-6 sm:h-6 ${text.muted} group-hover:text-purple-400 transition-colors`} />
                </a>
              </div>
            </div>

            {/* Right Side - Contact Form */}
            <div className="relative order-1 lg:order-2">
              <div 
                ref={formRef}
                className={`${background.card} rounded-2xl p-6 sm:p-8 shadow-2xl transition-transform duration-100 ease-out w-full`}
                style={{ transformStyle: 'preserve-3d' }}
              >
                <h3 className={`text-xl sm:text-2xl font-bold ${text.primary} mb-6 flex items-center`}>
                  <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 mr-3" />
                  Send a Message
                </h3>

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  {/* Name & Email Row */}
                  <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="group">
                      <label className={`block text-sm font-medium ${text.secondary} mb-2`}>
                        Full Name
                      </label>
                      <div className="relative">
                        <User className={`absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${text.muted} group-focus-within:text-blue-400 transition-colors`} />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 ${isDark ? 'bg-white/5 border-white/10 focus:bg-white/10' : 'bg-gray-50 border-gray-200 focus:bg-white'} border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${text.primary} ${isDark ? 'placeholder-gray-400' : 'placeholder-gray-500'} transition-all duration-300 hover:${isDark ? 'bg-white/10' : 'bg-gray-100'} text-sm sm:text-base`}
                          placeholder="Your name"
                        />
                      </div>
                    </div>

                    <div className="group">
                      <label className={`block text-sm font-medium ${text.secondary} mb-2`}>
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className={`absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${text.muted} group-focus-within:text-green-400 transition-colors`} />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 ${isDark ? 'bg-white/5 border-white/10 focus:bg-white/10' : 'bg-gray-50 border-gray-200 focus:bg-white'} border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 ${text.primary} ${isDark ? 'placeholder-gray-400' : 'placeholder-gray-500'} transition-all duration-300 hover:${isDark ? 'bg-white/10' : 'bg-gray-100'} text-sm sm:text-base`}
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="group">
                    <label className={`block text-sm font-medium ${text.secondary} mb-2`}>
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className={`w-full px-3 sm:px-4 py-3 sm:py-4 ${isDark ? 'bg-white/5 border-white/10 focus:bg-white/10' : 'bg-gray-50 border-gray-200 focus:bg-white'} border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${text.primary} ${isDark ? 'placeholder-gray-400' : 'placeholder-gray-500'} transition-all duration-300 hover:${isDark ? 'bg-white/10' : 'bg-gray-100'} text-sm sm:text-base`}
                      placeholder="What's this about?"
                    />
                  </div>

                  {/* Message */}
                  <div className="group">
                    <label className={`block text-sm font-medium ${text.secondary} mb-2`}>
                      Message
                    </label>
                    <div className="relative">
                      <MessageSquare className={`absolute left-3 sm:left-4 top-4 w-4 h-4 sm:w-5 sm:h-5 ${text.muted} group-focus-within:text-blue-400 transition-colors`} />
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 ${isDark ? 'bg-white/5 border-white/10 focus:bg-white/10' : 'bg-gray-50 border-gray-200 focus:bg-white'} border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${text.primary} ${isDark ? 'placeholder-gray-400' : 'placeholder-gray-500'} transition-all duration-300 hover:${isDark ? 'bg-white/10' : 'bg-gray-100'} resize-none text-sm sm:text-base`}
                        placeholder="Tell me about your iOS, iPadOS, or macOS project, or any opportunity you're looking to grow!"
                      ></textarea>
                    </div>
                  </div>

                  {/* Verification Challenge */}
                  {turnstileSiteKey && (
                    <div ref={turnstileRef} className="flex justify-center" />
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`group w-full relative overflow-hidden rounded-xl py-3 sm:py-4 px-6 font-semibold transition-all duration-300 text-sm sm:text-base ${
                      isSubmitting
                        ? `${isDark ? 'bg-gray-600' : 'bg-gray-400'} cursor-not-allowed`
                        : 'bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-400 hover:to-green-400 shadow-lg shadow-blue-500/25 hover:shadow-green-500/25 hover:scale-105'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2 relative z-10">
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                          <span className="text-white">Sending Message...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-12 transition-transform" />
                          <span className="text-white">Send Message</span>
                        </>
                      )}
                    </div>
                    
                    {/* Animated background */}
                    <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-blue-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                  </button>

                  {/* Success/Error Message */}
                  {submitStatus && (
                    <div className={`${
                      submitStatus.type === 'success'
                        ? `bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500/30 ${isDark ? 'text-green-300' : 'text-green-800'}`
                        : `bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-500/30 ${isDark ? 'text-red-300' : 'text-red-800'}`
                    } rounded-xl p-4 animate-fadeIn`}>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 ${
                          submitStatus.type === 'success' ? 'bg-green-400' : 'bg-red-400'
                        } rounded-full animate-pulse`}></div>
                        <span>{submitStatus.message}</span>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {/* Floating elements around form */}
              <div className="hidden sm:block absolute -top-4 -right-4 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg animate-bounce shadow-lg shadow-blue-500/50"></div>
              <div className="hidden sm:block absolute -bottom-4 -left-4 w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-lg animate-pulse shadow-lg shadow-green-500/50"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </section>
  );
};

export default ContactSection;